import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Shield, Users, Music, ListMusic, Activity, 
  TrendingUp, LogIn, Upload, Play, Search, UserCheck, UserX,
  RefreshCw, Trash2, Ban, Calendar, ChevronDown, ChevronUp,
  HardDrive, Database, Mail, Cloud, Server, Wrench, LifeBuoy, Sparkles, Zap, Cpu, MemoryStick, Coins, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import AdminSupportPanel from '@/components/AdminSupportPanel';
import AdminCoachPanel from '@/components/AdminCoachPanel';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [expandedUser, setExpandedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sysStats, setSysStats] = useState(null);
  const [analysisHealth, setAnalysisHealth] = useState(null);
  const [sysLoading, setSysLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [grantInput, setGrantInput] = useState({});   // userId -> custom amount string
  const [grantLoading, setGrantLoading] = useState(null); // userId being processed
  const hasFetched = useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) {
      navigate('/login');
      return;
    }
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, [authLoading, user, isAdmin, navigate]);

  // Auto-refresh System tab every 30s when the toggle is on
  useEffect(() => {
    if (!autoRefresh || activeTab !== 'system') return;
    const id = setInterval(() => { fetchSystemStats(); }, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/activity?limit=50`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setActivities(activitiesRes.data.activities);
    } catch (error) {
      toast.error('Failed to fetch admin data');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?is_active=${!currentStatus}`);
      toast.success(`User ${currentStatus ? 'frozen' : 'unfrozen'}`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User and all data deleted');
      setUsers(prev => prev.filter(u => u.id !== userId));
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const fetchSystemStats = async () => {
    setSysLoading(true);
    try {
      const [statsRes, healthRes] = await Promise.all([
        axios.get(`${API}/admin/system-stats`),
        axios.get(`${API}/admin/health/analysis`),
      ]);
      setSysStats(statsRes.data);
      setAnalysisHealth(healthRes.data);
    } catch (error) {
      toast.error('Failed to fetch system stats');
    } finally {
      setSysLoading(false);
    }
  };

  const runR2Cleanup = async () => {
    if (!window.confirm('Delete all orphaned R2 audio files (no matching DB track)?')) return;
    setCleanupRunning(true);
    try {
      const res = await axios.post(`${API}/admin/cleanup-r2-orphans`);
      const { orphans_found, deleted, failed, mb_freed } = res.data;
      if (orphans_found === 0) {
        toast.success('No orphans found — R2 is clean');
      } else {
        toast.success(`Cleaned ${deleted}/${orphans_found} orphans (${mb_freed} MB freed${failed ? `, ${failed} failed` : ''})`);
      }
      // Refresh stats after cleanup
      fetchSystemStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleanupRunning(false);
    }
  };

  const grantCredits = async (userId, amount, reason = 'Admin recharge') => {
    if (!amount || amount <= 0) return;
    setGrantLoading(userId);
    try {
      const res = await axios.post(`${API}/admin/credits/grant`, { user_id: userId, amount, reason });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, credits: res.data.new_balance } : u));
      setGrantInput(prev => ({ ...prev, [userId]: '' }));
      toast.success(`+${amount} credits added — new balance: ${res.data.new_balance}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to grant credits');
    } finally {
      setGrantLoading(null);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'Never';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Never';
    const d = new Date(isoString);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  };

  const getActionIcon = (action) => {
    const icons = {
      login: <LogIn className="w-4 h-4 text-green-400" />,
      signup: <UserCheck className="w-4 h-4 text-blue-400" />,
      upload_track: <Upload className="w-4 h-4 text-purple-400" />,
      play_track: <Play className="w-4 h-4 text-[#2563FF]" />,
      search: <Search className="w-4 h-4 text-yellow-400" />,
      profile_update: <Users className="w-4 h-4 text-cyan-400" />,
    };
    return icons[action] || <Activity className="w-4 h-4 text-[#64748B]" />;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#2563FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#EFF6FF] border border-[#2563FF]/50 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#2563FF]" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-[#0F172A]">ADMIN DASHBOARD</h1>
                <p className="font-body text-[#64748B]">Manage users and monitor activity</p>
              </div>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#E2E8F0]" data-testid="admin-refresh">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
              <div className="glass-pane-soft p-4">
                <Users className="w-6 h-6 text-[#2563FF] mb-2" />
                <p className="font-mono text-2xl text-[#0F172A]">{stats.users.total}</p>
                <p className="font-mono text-xs text-[#94A3B8] uppercase">Total Users</p>
              </div>
              <div className="glass-pane-soft p-4">
                <UserCheck className="w-6 h-6 text-green-500 mb-2" />
                <p className="font-mono text-2xl text-[#0F172A]">{stats.users.active}</p>
                <p className="font-mono text-xs text-[#94A3B8] uppercase">Active Users</p>
              </div>
              <div className="glass-pane-soft p-4">
                <Music className="w-6 h-6 text-purple-500 mb-2" />
                <p className="font-mono text-2xl text-[#0F172A]">{stats.content.tracks}</p>
                <p className="font-mono text-xs text-[#94A3B8] uppercase">Total Tracks</p>
              </div>
              <div className="glass-pane-soft p-4">
                <ListMusic className="w-6 h-6 text-blue-500 mb-2" />
                <p className="font-mono text-2xl text-[#0F172A]">{stats.content.playlists}</p>
                <p className="font-mono text-xs text-[#94A3B8] uppercase">Playlists</p>
              </div>
              <div className="glass-pane-soft p-4">
                <LogIn className="w-6 h-6 text-green-400 mb-2" />
                <p className="font-mono text-2xl text-[#0F172A]">{stats.activity_24h.logins}</p>
                <p className="font-mono text-xs text-[#94A3B8] uppercase">Logins (24h)</p>
              </div>
              <div className="glass-pane-soft p-4">
                <TrendingUp className="w-6 h-6 text-yellow-500 mb-2" />
                <p className="font-mono text-2xl text-[#0F172A]">{stats.users.new_this_week}</p>
                <p className="font-mono text-xs text-[#94A3B8] uppercase">New (7d)</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 font-mono text-sm ${
                activeTab === 'users' 
                  ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/50' 
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:text-[#0F172A]'
              }`}
              data-testid="admin-tab-users"
            >
              Users ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('activity')}
              className={`px-4 py-2 font-mono text-sm ${
                activeTab === 'activity' 
                  ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/50' 
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:text-[#0F172A]'
              }`}
              data-testid="admin-tab-activity"
            >
              Activity Feed
            </button>
            <button
              onClick={() => { setActiveTab('system'); if (!sysStats) fetchSystemStats(); }}
              className={`px-4 py-2 font-mono text-sm ${
                activeTab === 'system' 
                  ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/50' 
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:text-[#0F172A]'
              }`}
              data-testid="admin-tab-system"
            >
              System
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`px-4 py-2 font-mono text-sm flex items-center gap-1.5 ${
                activeTab === 'support'
                  ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/50'
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:text-[#0F172A]'
              }`}
              data-testid="admin-tab-support"
            >
              <LifeBuoy className="w-4 h-4" />
              Support
            </button>
            <button
              onClick={() => setActiveTab('coach')}
              className={`px-4 py-2 font-mono text-sm flex items-center gap-1.5 ${
                activeTab === 'coach'
                  ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/50'
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:text-[#0F172A]'
              }`}
              data-testid="admin-tab-coach"
            >
              <Sparkles className="w-4 h-4" />
              AI Coach
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`px-4 py-2 font-mono text-sm flex items-center gap-1.5 ${
                activeTab === 'subscriptions'
                  ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/50' 
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:text-[#0F172A]'
              }`}
              data-testid="admin-tab-subscriptions"
            >
              <Coins className="w-4 h-4" />
              Pro Access
            </button>
            <button
              onClick={() => setActiveTab('diagnostics')}
              className={`px-4 py-2 font-mono text-sm flex items-center gap-1.5 ${
                activeTab === 'diagnostics'
                  ? 'bg-[#EFF6FF] text-[#2563FF] border border-[#2563FF]/50' 
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:text-[#0F172A]'
              }`}
              data-testid="admin-tab-diagnostics"
            >
              <AlertCircle className="w-4 h-4" />
              Track Issues
            </button>
          </div>

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="bg-white border border-[#E2E8F0]" data-testid="admin-users-panel">
              <div className="p-4 border-b border-[#E2E8F0]">
                <h3 className="font-heading text-lg text-[#0F172A]">REGISTERED USERS</h3>
                <p className="font-mono text-xs text-[#94A3B8] mt-1">Sorted by registration date (newest first)</p>
              </div>

              {/* Table Header */}
              <div className="hidden lg:grid grid-cols-12 gap-2 px-4 py-3 text-xs font-mono text-[#64748B] uppercase tracking-wider border-b border-[#E2E8F0] bg-[#F8FAFC]">
                <div className="col-span-3">User</div>
                <div className="col-span-2">Registered</div>
                <div className="col-span-1 text-center">Tracks</div>
                <div className="col-span-1 text-center">Status</div>
                <div className="col-span-3">Credits</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>

              <div className="divide-y divide-[#E2E8F0]">
                {users.map((u) => (
                  <div key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    {/* Desktop Row */}
                    <div className="hidden lg:grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-[#F8FAFC] transition-colors">
                      <div className="col-span-3 flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-[#EFF6FF] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-[#1E3A8A]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-heading text-sm text-[#0F172A] truncate">{u.name}</span>
                            {u.role === 'admin' && (
                              <span className="px-1.5 py-0.5 bg-[#EFF6FF] text-[#2563FF] border border-[#E2E8F0] text-[10px] font-mono">ADMIN</span>
                            )}
                          </div>
                          <p className="font-mono text-xs text-[#94A3B8] truncate">{u.email}</p>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <p className="font-mono text-xs text-[#64748B]">{formatDate(u.created_at)}</p>
                        <p className="font-mono text-[10px] text-[#94A3B8]">Last: {formatDate(u.last_login)}</p>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className="font-mono text-sm text-purple-400">{u.track_count || 0}</span>
                      </div>
                      <div className="col-span-1 text-center">
                        {u.is_active ? (
                          <span className="px-2 py-0.5 bg-green-950/30 text-green-400 border border-green-900/30 text-[10px] font-mono">ACTIVE</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-[#EFF6FF] text-[#2563FF] border border-[#E2E8F0] text-[10px] font-mono">FROZEN</span>
                        )}
                      </div>

                      {/* Credits column */}
                      <div className="col-span-3 flex items-center gap-2">
                        <div className="flex items-center gap-1 min-w-[52px]">
                          <Coins className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                          <span className="font-mono text-sm font-bold text-[#0F172A]">{u.credits ?? 0}</span>
                        </div>
                        {/* Quick presets */}
                        {[50, 100, 200].map(amt => (
                          <button
                            key={amt}
                            onClick={() => grantCredits(u.id, amt)}
                            disabled={grantLoading === u.id}
                            className="px-2 py-0.5 rounded bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] font-mono text-[11px] hover:bg-[#DBEAFE] transition-colors disabled:opacity-50"
                            data-testid={`grant-${amt}-${u.id}`}
                            title={`Add ${amt} credits`}
                          >+{amt}</button>
                        ))}
                        {/* Custom input */}
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="1"
                            placeholder="amt"
                            value={grantInput[u.id] || ''}
                            onChange={e => setGrantInput(prev => ({ ...prev, [u.id]: e.target.value }))}
                            className="w-14 px-1.5 py-0.5 border border-[#E2E8F0] font-mono text-[11px] text-[#0F172A] focus:border-[#2563FF] outline-none rounded"
                            data-testid={`grant-input-${u.id}`}
                          />
                          <button
                            onClick={() => grantCredits(u.id, parseInt(grantInput[u.id] || 0))}
                            disabled={grantLoading === u.id || !grantInput[u.id]}
                            className="px-2 py-0.5 rounded bg-[#2563FF] text-white font-mono text-[11px] hover:bg-[#1D4ED8] transition-colors disabled:opacity-40"
                            data-testid={`grant-custom-${u.id}`}
                          >Add</button>
                        </div>
                      </div>

                      <div className="col-span-2 flex justify-end gap-2">
                        {u.id !== user.id && u.role !== 'admin' && (
                          <>
                            <Button
                              onClick={() => toggleUserStatus(u.id, u.is_active)}
                              variant="outline"
                              size="sm"
                              className={`text-xs h-7 ${u.is_active ? 'border-yellow-900/50 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-950/20' : 'border-green-900/50 text-green-400 hover:text-green-300 hover:bg-green-950/20'}`}
                              data-testid={`freeze-user-${u.id}`}
                            >
                              {u.is_active ? <><Ban className="w-3 h-3 mr-1" />Freeze</> : <><UserCheck className="w-3 h-3 mr-1" />Unfreeze</>}
                            </Button>
                            {deleteConfirm === u.id ? (
                              <div className="flex gap-1">
                                <Button
                                  onClick={() => deleteUser(u.id)}
                                  size="sm"
                                  className="text-xs h-7 bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A]"
                                  data-testid={`confirm-delete-${u.id}`}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  onClick={() => setDeleteConfirm(null)}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs h-7 border-gray-700 text-[#64748B]"
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                onClick={() => setDeleteConfirm(u.id)}
                                variant="outline"
                                size="sm"
                                className="text-xs h-7 border-[#E2E8F0] text-[#2563FF] hover:text-[#3B82F6] hover:bg-[#F1F5F9]"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-3 h-3 mr-1" />Delete
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Mobile Row */}
                    <div className="lg:hidden p-4">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#EFF6FF] border border-[#E2E8F0] flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#1E3A8A]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-heading text-sm text-[#0F172A]">{u.name}</span>
                              {u.role === 'admin' && (
                                <span className="px-1.5 py-0.5 bg-[#EFF6FF] text-[#2563FF] border border-[#E2E8F0] text-[10px] font-mono">ADMIN</span>
                              )}
                              {!u.is_active && (
                                <span className="px-1.5 py-0.5 bg-[#EFF6FF] text-[#2563FF] border border-[#E2E8F0] text-[10px] font-mono">FROZEN</span>
                              )}
                            </div>
                            <p className="font-mono text-xs text-[#94A3B8]">{u.email}</p>
                          </div>
                        </div>
                        {expandedUser === u.id ? <ChevronUp className="w-5 h-5 text-[#64748B]" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" />}
                      </div>

                      {expandedUser === u.id && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 pt-4 border-t border-[#E2E8F0]">
                          <div className="grid grid-cols-2 gap-3 mb-4">
                            <div>
                              <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Registered</p>
                              <p className="font-mono text-xs text-[#64748B] flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(u.created_at)}</p>
                            </div>
                            <div>
                              <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Last Login</p>
                              <p className="font-mono text-xs text-[#64748B]">{formatDate(u.last_login)}</p>
                            </div>
                            <div>
                              <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Tracks</p>
                              <p className="font-mono text-sm text-purple-400">{u.track_count || 0}</p>
                            </div>
                            <div>
                              <p className="font-mono text-[10px] text-[#94A3B8] uppercase">Status</p>
                              <p className={`font-mono text-xs ${u.is_active ? 'text-green-400' : 'text-[#2563FF]'}`}>{u.is_active ? 'Active' : 'Frozen'}</p>
                            </div>
                          </div>
                          {/* Mobile credits grant */}
                          <div className="mb-4">
                            <p className="font-mono text-[10px] text-[#94A3B8] uppercase mb-2">Credits — <span className="text-yellow-500 font-bold">{u.credits ?? 0}</span></p>
                            <div className="flex flex-wrap gap-2">
                              {[50, 100, 200].map(amt => (
                                <button key={amt} onClick={() => grantCredits(u.id, amt)} disabled={grantLoading === u.id}
                                  className="px-3 py-1 rounded bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] font-mono text-xs hover:bg-[#DBEAFE] disabled:opacity-50">
                                  +{amt}
                                </button>
                              ))}
                              <div className="flex items-center gap-1">
                                <input type="number" min="1" placeholder="custom" value={grantInput[u.id] || ''}
                                  onChange={e => setGrantInput(prev => ({ ...prev, [u.id]: e.target.value }))}
                                  className="w-20 px-2 py-1 border border-[#E2E8F0] font-mono text-xs text-[#0F172A] focus:border-[#2563FF] outline-none rounded" />
                                <button onClick={() => grantCredits(u.id, parseInt(grantInput[u.id] || 0))} disabled={grantLoading === u.id || !grantInput[u.id]}
                                  className="px-3 py-1 rounded bg-[#2563FF] text-white font-mono text-xs hover:bg-[#1D4ED8] disabled:opacity-40">
                                  Add
                                </button>
                              </div>
                            </div>
                          </div>
                          {u.id !== user.id && u.role !== 'admin' && (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => toggleUserStatus(u.id, u.is_active)}
                                variant="outline"
                                size="sm"
                                className={`text-xs ${u.is_active ? 'border-yellow-900/50 text-yellow-400' : 'border-green-900/50 text-green-400'}`}
                              >
                                {u.is_active ? <><Ban className="w-3 h-3 mr-1" />Freeze</> : <><UserCheck className="w-3 h-3 mr-1" />Unfreeze</>}
                              </Button>
                              {deleteConfirm === u.id ? (
                                <div className="flex gap-1">
                                  <Button onClick={() => deleteUser(u.id)} size="sm" className="text-xs bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A]">Confirm</Button>
                                  <Button onClick={() => setDeleteConfirm(null)} variant="outline" size="sm" className="text-xs border-gray-700 text-[#64748B]">Cancel</Button>
                                </div>
                              ) : (
                                <Button onClick={() => setDeleteConfirm(u.id)} variant="outline" size="sm" className="text-xs border-[#E2E8F0] text-[#2563FF]">
                                  <Trash2 className="w-3 h-3 mr-1" />Delete
                                </Button>
                              )}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity Feed Tab */}
          {activeTab === 'activity' && (
            <div className="bg-white border border-[#E2E8F0]">
              <div className="p-4 border-b border-[#E2E8F0]">
                <h3 className="font-heading text-lg text-[#0F172A]">RECENT ACTIVITY</h3>
              </div>
              <div className="max-h-[600px] overflow-y-auto">
                {activities.length === 0 ? (
                  <div className="p-8 text-center text-[#64748B]">
                    <Activity className="w-12 h-12 mx-auto mb-3 text-[#1E3A8A]" />
                    <p>No activity recorded yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#E2E8F0]">
                    {activities.map((activity) => (
                      <div key={activity.id} className="p-4 hover:bg-[#F8FAFC] transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="mt-1">{getActionIcon(activity.action)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-heading text-[#0F172A]">{activity.user_name || 'Unknown'}</span>
                              <span className="font-mono text-xs text-[#94A3B8]">{activity.user_email}</span>
                            </div>
                            <p className="font-body text-[#64748B]">
                              <span className="text-[#2563FF]">{activity.action.replace('_', ' ')}</span>
                              {activity.details?.title && <span> - {activity.details.title}</span>}
                            </p>
                            <p className="font-mono text-xs text-[#94A3B8] mt-1">{formatDateTime(activity.timestamp)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* System Stats Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6" data-testid="admin-system-panel">
              {/* Action bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#E2E8F0] p-4">
                <div>
                  <h3 className="font-heading text-lg text-[#0F172A]">SYSTEM HEALTH</h3>
                  <p className="font-mono text-xs text-[#94A3B8] mt-1 flex items-center gap-2">
                    {sysStats ? `Last refreshed ${new Date(sysStats.generated_at).toLocaleTimeString()}` : 'Loading…'}
                    {autoRefresh && (
                      <span className="inline-flex items-center gap-1 text-emerald-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        LIVE · 30s
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAutoRefresh((v) => !v)}
                    className={`flex items-center gap-2 px-3 py-2 font-mono text-xs border transition-colors ${
                      autoRefresh
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                        : 'bg-white text-[#64748B] border-[#E2E8F0] hover:text-[#0F172A]'
                    }`}
                    data-testid="admin-system-auto-refresh"
                    title="Auto-refresh every 30s"
                  >
                    <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-emerald-500 animate-pulse' : 'bg-[#94A3B8]'}`} />
                    {autoRefresh ? 'LIVE' : 'AUTO-REFRESH'}
                  </button>
                  <Button
                    onClick={fetchSystemStats}
                    variant="outline"
                    className="border-[#E2E8F0]"
                    disabled={sysLoading}
                    data-testid="admin-system-refresh"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${sysLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button
                    onClick={runR2Cleanup}
                    disabled={cleanupRunning || !sysStats?.r2?.configured}
                    className="bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A]"
                    data-testid="admin-cleanup-r2"
                  >
                    <Wrench className={`w-4 h-4 mr-2 ${cleanupRunning ? 'animate-pulse' : ''}`} />
                    {cleanupRunning ? 'Cleaning…' : 'Clean R2 Orphans'}
                  </Button>
                </div>
              </div>

              {sysStats && (
                <>
                  {/* ── Server Resource Meters ── */}
                  <div className="bg-white border border-[#E2E8F0] p-5 space-y-5">
                    <h4 className="font-heading text-sm text-[#0F172A] uppercase flex items-center gap-2">
                      <Server className="w-4 h-4 text-[#2563FF]" /> Server Resources
                    </h4>

                    {/* CPU */}
                    <ResourceMeter
                      label="CPU"
                      sub={`${sysStats.server.cpu.cores} cores · load ${sysStats.server.cpu.load_1m} / ${sysStats.server.cpu.load_5m} / ${sysStats.server.cpu.load_15m}`}
                      pct={sysStats.server.cpu.system_pct}
                      peaking={sysStats.server.cpu.peaking}
                      extra={`Process: ${sysStats.server.cpu.process_pct}%`}
                      testId="meter-cpu"
                    />

                    {/* Memory */}
                    <ResourceMeter
                      label="Memory"
                      sub={`${formatBytes(sysStats.server.memory.used_bytes)} / ${formatBytes(sysStats.server.memory.total_bytes)} · process ${formatBytes(sysStats.server.memory.process_rss)}`}
                      pct={sysStats.server.memory.percent}
                      peaking={sysStats.server.memory.peaking}
                      testId="meter-memory"
                    />

                    {/* Disk */}
                    <ResourceMeter
                      label="Disk"
                      sub={`${formatBytes(sysStats.server.disk.used_bytes)} used · ${formatBytes(sysStats.server.disk.free_bytes)} free`}
                      pct={sysStats.server.disk.percent}
                      peaking={sysStats.server.disk.peaking}
                      testId="meter-disk"
                    />

                    {/* Swap */}
                    {sysStats.server.swap.total_bytes > 0 && (
                      <ResourceMeter
                        label="Swap"
                        sub={`${formatBytes(sysStats.server.swap.used_bytes)} / ${formatBytes(sysStats.server.swap.total_bytes)}`}
                        pct={sysStats.server.swap.percent}
                        peaking={sysStats.server.swap.percent > 50}
                        testId="meter-swap"
                      />
                    )}

                    {/* Process info */}
                    <div className="grid grid-cols-3 gap-3 pt-1">
                      {[
                        { label: 'PID',      value: sysStats.server.process.pid },
                        { label: 'Threads',  value: sysStats.server.process.threads },
                        { label: 'Open FDs', value: sysStats.server.process.open_fds },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-[#F5F7FB] rounded-lg px-3 py-2 text-center">
                          <p className="font-mono text-base font-bold text-[#0F172A]">{value}</p>
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Analysis Worker Queue ── */}
                  <div className="bg-white border border-[#E2E8F0] p-5">
                    <h4 className="font-heading text-sm text-[#0F172A] uppercase flex items-center gap-2 mb-4">
                      <Activity className="w-4 h-4 text-[#2563FF]" /> Analysis Worker Queue
                      {sysStats.worker_queue.busy && (
                        <span className="ml-auto text-[10px] font-heading bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full animate-pulse">
                          BUSY
                        </span>
                      )}
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: 'Pending',    value: sysStats.worker_queue.pending,    color: '#F59E0B' },
                        { label: 'Processing', value: sysStats.worker_queue.processing, color: '#2563FF' },
                        { label: 'Completed',  value: sysStats.worker_queue.done,       color: '#10B981' },
                        { label: 'Failed',     value: sysStats.worker_queue.failed,     color: '#EF4444' },
                      ].map(({ label, value, color }) => (
                        <div key={label} className="text-center bg-[#F5F7FB] rounded-xl py-3 px-2">
                          <p className="font-mono text-2xl font-bold" style={{ color }}>{value}</p>
                          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mt-0.5">{label}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* ── Analysis Pipeline Health (30-day history + perf) ── */}
                  {analysisHealth && (
                    <AnalysisHealthPanel data={analysisHealth} />
                  )}

                  {/* ── Credit Activity (24h) ── */}
                  <div className="bg-white border border-[#E2E8F0] p-5">
                    <h4 className="font-heading text-sm text-[#0F172A] uppercase flex items-center gap-2 mb-4">
                      <Zap className="w-4 h-4 text-[#2563FF]" /> Credit Activity (last 24h)
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center bg-[#F5F7FB] rounded-xl py-3 px-2">
                        <p className="font-mono text-2xl font-bold text-[#2563FF]">{sysStats.credits_24h.transactions}</p>
                        <p className="font-mono text-[10px] text-[#94A3B8] uppercase mt-0.5">Transactions</p>
                      </div>
                      <div className="text-center bg-[#F5F7FB] rounded-xl py-3 px-2">
                        <p className="font-mono text-2xl font-bold text-[#EF4444]">{sysStats.credits_24h.credits_spent}</p>
                        <p className="font-mono text-[10px] text-[#94A3B8] uppercase mt-0.5">Credits Spent</p>
                      </div>
                    </div>
                  </div>

                  {/* ── Storage (MongoDB + R2) ── */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                      icon={<Database className="w-5 h-5 text-green-400" />}
                      label="Mongo Data"
                      value={formatBytes(sysStats.mongo.data_bytes)}
                      sub={`${sysStats.mongo.objects} objects`}
                    />
                    <StatCard
                      icon={<HardDrive className="w-5 h-5 text-blue-400" />}
                      label="Mongo Storage"
                      value={formatBytes(sysStats.mongo.storage_bytes)}
                      sub={`Indexes: ${formatBytes(sysStats.mongo.index_bytes)}`}
                    />
                    <StatCard
                      icon={<Cloud className="w-5 h-5 text-purple-400" />}
                      label="R2 Audio Storage"
                      value={sysStats.r2.configured ? formatBytes(sysStats.r2.total_bytes) : 'Not configured'}
                      sub={sysStats.r2.configured ? `${sysStats.r2.object_count} objects · ${sysStats.r2.bucket}` : ''}
                      error={sysStats.r2.error}
                    />
                    <StatCard
                      icon={<Music className="w-5 h-5 text-[#2563FF]" />}
                      label="Tracks with Audio"
                      value={sysStats.tracks_with_audio.toLocaleString()}
                      sub="has_audio=true references"
                    />
                  </div>

                  {/* Today's activity */}
                  <div className="glass-pane-soft p-4">
                    <h4 className="font-heading text-sm text-[#0F172A] uppercase mb-3 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-[#2563FF]" />
                      Today (UTC)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="font-mono text-2xl text-[#0F172A]">{sysStats.today.tracks_created}</p>
                        <p className="font-mono text-xs text-[#64748B] uppercase">Tracks created</p>
                      </div>
                      <div>
                        <p className="font-mono text-2xl text-[#0F172A]">{sysStats.today.tracks_created_yesterday}</p>
                        <p className="font-mono text-xs text-[#64748B] uppercase">Tracks yesterday</p>
                      </div>
                      <div>
                        <p className="font-mono text-2xl text-[#0F172A]">{sysStats.today.activity_events}</p>
                        <p className="font-mono text-xs text-[#64748B] uppercase">Activity events</p>
                      </div>
                      <div>
                        <p className="font-mono text-2xl text-[#0F172A]">{sysStats.today.emails_sent}</p>
                        <p className="font-mono text-xs text-[#64748B] uppercase">Emails sent</p>
                      </div>
                    </div>
                  </div>

                  {/* Collections breakdown */}
                  <div className="glass-pane-soft p-4">
                    <h4 className="font-heading text-sm text-[#0F172A] uppercase mb-3 flex items-center gap-2">
                      <Server className="w-4 h-4 text-[#2563FF]" />
                      Mongo Collections
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 font-mono text-sm">
                      {Object.entries(sysStats.collections).map(([name, count]) => (
                        <div key={name} className="flex justify-between items-center border-b border-[#E2E8F0] pb-1">
                          <span className="text-[#64748B]">{name}</span>
                          <span className="text-[#0F172A]">{count.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* External limits reference */}
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 text-xs font-mono text-[#64748B]">
                    <p className="uppercase text-[#64748B] mb-2 flex items-center gap-2">
                      <Mail className="w-3 h-3" />
                      External Dashboards (open to see exact quotas)
                    </p>
                    <ul className="space-y-1">
                      <li>• <a className="text-[#2563FF] hover:text-[#3B82F6]" href="https://dash.cloudflare.com/?to=/:account/r2" target="_blank" rel="noreferrer">Cloudflare R2</a> — storage, class-A/B ops, egress</li>
                      <li>• <a className="text-[#2563FF] hover:text-[#3B82F6]" href="https://cloud.mongodb.com" target="_blank" rel="noreferrer">MongoDB Atlas</a> — cluster tier, connections, IOPS</li>
                      <li>• <a className="text-[#2563FF] hover:text-[#3B82F6]" href="https://resend.com/emails" target="_blank" rel="noreferrer">Resend</a> — daily / monthly send quota</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && <AdminSupportPanel />}


          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <SubscriptionManagement />
          )}

          {/* AI Coach Tab */}
          {activeTab === 'coach' && <AdminCoachPanel />}

          {/* Track Diagnostics Tab */}
          {activeTab === 'diagnostics' && (
            <TrackDiagnostics />
          )}
        </motion.div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, error }) {
  return (
    <div className="glass-pane-soft p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="font-mono text-xs text-[#64748B] uppercase">{label}</p>
      </div>
      <p className="font-mono text-xl text-[#0F172A] truncate" title={String(value)}>{value}</p>
      {sub && <p className="font-mono text-[10px] text-[#94A3B8] mt-1 truncate" title={sub}>{sub}</p>}
      {error && <p className="font-mono text-[10px] text-[#2563FF] mt-1 truncate" title={error}>⚠ {error}</p>}
    </div>
  );
}

function ResourceMeter({ label, sub, pct, peaking, extra, testId }) {
  const isFull = pct >= 100;
  const isWarning = pct >= 80;
  const color = peaking ? '#EF4444' : pct > 60 ? '#F59E0B' : '#10B981';
  const bg    = peaking ? 'bg-red-50 border-red-200' : 'bg-white border-[#E2E8F0]';
  return (
    <div className={`rounded-xl border p-4 ${bg}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="font-heading text-sm text-[#0F172A]">{label}</span>
          {sub && <p className="font-mono text-[10px] text-[#94A3B8] mt-0.5">{sub}</p>}
        </div>
        <div className="text-right">
          <span className="font-mono text-2xl font-bold" style={{ color }}>{pct}%</span>
          {peaking && (
            <span className="ml-2 text-[10px] font-heading bg-red-100 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-full animate-pulse">
              PEAK
            </span>
          )}
        </div>
      </div>
      <div className="h-2 bg-[#F5F7FB] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.min(pct, 100)}%`, background: color }}
        />
      </div>
      {extra && <p className="font-mono text-[10px] text-[#94A3B8] mt-1">{extra}</p>}
    </div>
  );
}

function AnalysisHealthPanel({ data }) {
  const w = data.window || {};
  const t = data.timing_7d || {};
  const retries = data.retry_dist_30d || {};
  const failures = data.top_failures_30d || [];
  const stuck = data.stuck_jobs || [];
  const busy = data.busiest_days_30d || [];
  const cfg = data.worker_config || {};
  const totalRetried = Object.values(retries).reduce((a, b) => a + b, 0);
  const successColor =
    w.success_rate_pct === null ? '#94A3B8'
    : w.success_rate_pct >= 95 ? '#10B981'
    : w.success_rate_pct >= 80 ? '#F59E0B'
    : '#EF4444';

  return (
    <div className="bg-white border border-[#E2E8F0] p-5 space-y-5" data-testid="admin-analysis-health">
      <div className="flex items-center justify-between">
        <h4 className="font-heading text-sm text-[#0F172A] uppercase flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#2563FF]" /> Analysis Pipeline Health (30 days)
        </h4>
        {stuck.length > 0 && (
          <span className="text-[10px] font-heading bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-full animate-pulse">
            {stuck.length} STUCK
          </span>
        )}
      </div>

      {/* Top-line KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="text-center bg-[#F5F7FB] rounded-xl py-3 px-2" data-testid="kpi-success-rate">
          <p className="font-mono text-2xl font-bold" style={{ color: successColor }}>
            {w.success_rate_pct === null ? '—' : `${w.success_rate_pct}%`}
          </p>
          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mt-0.5">Success rate</p>
        </div>
        <div className="text-center bg-[#F5F7FB] rounded-xl py-3 px-2" data-testid="kpi-total-30d">
          <p className="font-mono text-2xl font-bold text-[#0F172A]">{w.total_30d ?? 0}</p>
          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mt-0.5">Jobs (30d)</p>
        </div>
        <div className="text-center bg-[#F5F7FB] rounded-xl py-3 px-2" data-testid="kpi-p95">
          <p className="font-mono text-2xl font-bold text-[#2563FF]">
            {t.p95_s != null ? `${t.p95_s}s` : '—'}
          </p>
          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mt-0.5">p95 duration (7d)</p>
        </div>
        <div className="text-center bg-[#F5F7FB] rounded-xl py-3 px-2" data-testid="kpi-p50">
          <p className="font-mono text-2xl font-bold text-[#0F172A]">
            {t.p50_s != null ? `${t.p50_s}s` : '—'}
          </p>
          <p className="font-mono text-[10px] text-[#94A3B8] uppercase mt-0.5">p50 duration (7d)</p>
        </div>
      </div>

      {/* Recent windows */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 font-mono text-xs">
        {[
          { label: 'Last 1 hour',  obj: w.by_status_1h },
          { label: 'Last 24 hours', obj: w.by_status_24h },
          { label: 'Last 30 days', obj: w.by_status_30d },
        ].map(({ label, obj }) => {
          const entries = Object.entries(obj || {});
          return (
            <div key={label} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
              <p className="text-[10px] uppercase text-[#94A3B8] mb-2">{label}</p>
              {entries.length === 0 ? (
                <p className="text-[#94A3B8]">no jobs</p>
              ) : entries.map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-[#E2E8F0] last:border-0 py-0.5">
                  <span className="text-[#64748B] capitalize">{k}</span>
                  <span className="text-[#0F172A]">{v}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Stuck jobs (if any) */}
      {stuck.length > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-lg p-3" data-testid="stuck-jobs-panel">
          <p className="font-heading text-xs text-red-700 uppercase mb-2 flex items-center gap-2">
            <Ban className="w-3 h-3" /> Currently stuck in &apos;processing&apos; &gt; 3 min
          </p>
          <div className="space-y-1 font-mono text-[11px]">
            {stuck.slice(0, 10).map((j) => (
              <div key={j.job_id} className="flex justify-between text-red-800">
                <span className="truncate">track {j.track_id?.slice(0, 8)}… · retry {j.retry_count}/3</span>
                <span className="text-red-500">{j.started_at?.slice(11, 19)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top failure reasons */}
      {failures.length > 0 && (
        <div>
          <p className="font-heading text-xs text-[#0F172A] uppercase mb-2 flex items-center gap-2">
            <Ban className="w-3 h-3 text-red-500" /> Top failure reasons (30d)
          </p>
          <div className="space-y-1 font-mono text-xs" data-testid="top-failures">
            {failures.map((f, i) => (
              <div key={i} className="flex justify-between gap-3 border-b border-[#E2E8F0] py-1">
                <span className="text-[#64748B] truncate" title={f.reason}>{f.reason}</span>
                <span className="text-red-600 font-bold shrink-0">×{f.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Retry distribution */}
      {totalRetried > 0 && (
        <div>
          <p className="font-heading text-xs text-[#0F172A] uppercase mb-2 flex items-center gap-2">
            <RefreshCw className="w-3 h-3 text-amber-500" /> Retry distribution (30d)
            <span className="font-mono text-[10px] text-[#94A3B8] normal-case">
              {totalRetried} jobs auto-recovered by watchdog
            </span>
          </p>
          <div className="grid grid-cols-3 gap-2 font-mono text-xs">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-2 text-center">
                <p className="text-base text-[#0F172A] font-bold">{retries[n] || 0}</p>
                <p className="text-[10px] text-[#94A3B8] uppercase">retried {n}×</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Busiest days */}
      {busy.length > 0 && (
        <div>
          <p className="font-heading text-xs text-[#0F172A] uppercase mb-2 flex items-center gap-2">
            <Calendar className="w-3 h-3 text-[#2563FF]" /> Busiest days (top 7)
          </p>
          <div className="space-y-1 font-mono text-xs" data-testid="busiest-days">
            {busy.map((d) => {
              const max = busy[0].count || 1;
              const pct = (d.count / max) * 100;
              return (
                <div key={d.day} className="flex items-center gap-3">
                  <span className="text-[#64748B] w-24 shrink-0">{d.day}</span>
                  <div className="flex-1 h-2 bg-[#F5F7FB] rounded-full overflow-hidden">
                    <div className="h-full bg-[#2563FF] rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[#0F172A] w-10 text-right">{d.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Worker config snapshot */}
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
        <p className="font-heading text-[10px] uppercase text-[#94A3B8] mb-2">Active worker limits</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 font-mono text-[11px]">
          <ConfigPill label="concurrency"      value={cfg.concurrency} />
          <ConfigPill label="timeout"          value={`${cfg.analysis_timeout_s}s`} />
          <ConfigPill label="audio cap"        value={`${cfg.audio_truncation_s}s`} />
          <ConfigPill label="stale threshold"  value={`${cfg.stale_processing_minutes}min`} />
          <ConfigPill label="watchdog every"   value={`${cfg.watchdog_interval_s}s`} />
        </div>
      </div>

      <p className="font-mono text-[10px] text-[#94A3B8] text-right">
        Generated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : '—'}
        {t.samples != null && ` · timing sampled from ${t.samples} jobs`}
      </p>
    </div>
  );
}

function ConfigPill({ label, value }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded px-2 py-1 text-center">
      <p className="text-[#0F172A] font-bold">{value}</p>
      <p className="text-[9px] uppercase text-[#94A3B8]">{label}</p>
    </div>
  );
}


function SubscriptionManagement() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('active');
  const [credits, setCredits] = useState(10000);
  const [loading, setLoading] = useState(false);

  const handleUpdateSubscription = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/subscription/update`, {
        email: email.trim(),
        subscription_status: status,
        credits: parseInt(credits)
      });
      
      toast.success(response.data.message);
      setEmail('');
      setStatus('active');
      setCredits(10000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass-pane-soft p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-[#0F172A] mb-2">Pro Access Management</h2>
        <p className="text-sm text-[#64748B]">
          Grant or revoke pro subscription access for any user by email
        </p>
      </div>

      <form onSubmit={handleUpdateSubscription} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">
            User Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2563FF]"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">
            Subscription Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2563FF]"
          >
            <option value="active">Active (Grant Pro)</option>
            <option value="none">None (Revoke Pro)</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {status === 'active' && (
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Credits to Grant
            </label>
            <input
              type="number"
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              min="0"
              step="100"
              className="w-full px-4 py-2 border border-[#E2E8F0] rounded-lg focus:outline-none focus:border-[#2563FF]"
            />
            <p className="text-xs text-[#64748B] mt-1">
              Default: 10,000 credits (1 year pro subscription)
            </p>
          </div>
        )}

        <div className="flex gap-4">
          <Button
            type="submit"
            disabled={loading}
            className="bg-[#2563FF] hover:bg-[#3B82F6] text-white"
          >
            {loading ? 'Updating...' : status === 'active' ? 'Grant Pro Access' : 'Revoke Pro Access'}
          </Button>
          <Button
            type="button"
            onClick={() => {
              setEmail('');
              setStatus('active');
              setCredits(10000);
            }}
            variant="outline"
            className="border-[#E2E8F0]"
          >
            Reset Form
          </Button>
        </div>
      </form>

      <div className="mt-8 p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
        <h3 className="font-medium text-[#0F172A] mb-2">Quick Actions</h3>
        <div className="space-y-2 text-sm text-[#64748B]">
          <p>• <strong>Active:</strong> User gets pro features + credits for 1 year</p>
          <p>• <strong>None:</strong> Removes pro status and sets credits to 0</p>
          <p>• <strong>Cancelled:</strong> Marks subscription as cancelled</p>
        </div>
      </div>
    </div>
  );
}

function TrackDiagnostics() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/debug/track-issues`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to fetch diagnostics');
      } finally {
        setLoading(false);
      }
    };
    fetchDiagnostics();
  }, []);

  if (loading) {
    return (
      <div className="glass-pane-soft p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563FF] mx-auto"></div>
          <p className="mt-4 text-[#64748B]">Analyzing tracks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-pane-soft p-6">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  const { summary, missing_artist, failed_analysis, missing_cover, stuck_at_raw } = data;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-pane-soft p-4">
          <div className="text-2xl font-bold text-[#0F172A]">{summary.total_tracks}</div>
          <div className="text-sm text-[#64748B]">Total Tracks</div>
        </div>
        <div className="glass-pane-soft p-4">
          <div className="text-2xl font-bold text-orange-600">{summary.missing_artist_count}</div>
          <div className="text-sm text-[#64748B]">Missing Artist</div>
        </div>
        <div className="glass-pane-soft p-4">
          <div className="text-2xl font-bold text-red-600">{summary.failed_analysis_count}</div>
          <div className="text-sm text-[#64748B]">Failed Analysis</div>
        </div>
        <div className="glass-pane-soft p-4">
          <div className="text-2xl font-bold text-blue-600">{summary.missing_cover_count}</div>
          <div className="text-sm text-[#64748B]">Missing Covers</div>
        </div>
      </div>

      {/* Failed Analysis */}
      {failed_analysis.length > 0 && (
        <div className="glass-pane-soft p-6">
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">
            ❌ Failed Analysis ({failed_analysis.length})
          </h3>
          <div className="space-y-2">
            {failed_analysis.slice(0, 10).map((track, idx) => (
              <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="font-medium text-[#0F172A]">{track.title}</div>
                <div className="text-sm text-[#64748B]">{track.filename}</div>
                <div className="text-xs text-red-600 mt-1">{track.reason}</div>
              </div>
            ))}
            {failed_analysis.length > 10 && (
              <div className="text-sm text-[#64748B] text-center">
                ...and {failed_analysis.length - 10} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Missing Artist */}
      {missing_artist.length > 0 && (
        <div className="glass-pane-soft p-6">
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">
            👤 Missing Artist ({missing_artist.length})
          </h3>
          <div className="space-y-2">
            {missing_artist.slice(0, 10).map((track, idx) => (
              <div key={idx} className="p-3 bg-orange-50 border border-orange-200 rounded">
                <div className="font-medium text-[#0F172A]">{track.title}</div>
                <div className="text-sm text-[#64748B]">{track.filename}</div>
              </div>
            ))}
            {missing_artist.length > 10 && (
              <div className="text-sm text-[#64748B] text-center">
                ...and {missing_artist.length - 10} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Missing Covers */}
      {missing_cover.length > 0 && (
        <div className="glass-pane-soft p-6">
          <h3 className="text-lg font-bold text-[#0F172A] mb-4">
            🖼️ Missing Cover Art ({missing_cover.length})
          </h3>
          <div className="space-y-2">
            {missing_cover.slice(0, 10).map((track, idx) => (
              <div key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="font-medium text-[#0F172A]">{track.title}</div>
                <div className="text-sm text-[#64748B]">{track.filename}</div>
              </div>
            ))}
            {missing_cover.length > 10 && (
              <div className="text-sm text-[#64748B] text-center">
                ...and {missing_cover.length - 10} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Good */}
      {failed_analysis.length === 0 && missing_artist.length === 0 && missing_cover.length === 0 && (
        <div className="glass-pane-soft p-12 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h3 className="text-2xl font-bold text-[#0F172A] mb-2">All Tracks Healthy!</h3>
          <p className="text-[#64748B]">No issues detected with track metadata, analysis, or covers.</p>
        </div>
      )}
    </div>
  );
}


